import axios from "axios";
import { FindInstruments } from "@/models/find-instruments";
import { isOmnisearchEnabled } from "@/lib/omnisearch/flag";
import { OmniKind, OmniResponse, OmniResult } from "@/lib/omnisearch/types";

export const findInstruments = async (text: string, refresh?: boolean) => {
    const res = await axios.get<FindInstruments[]>(`/API/v2/find/0/${text}`, {
        params: refresh
            ? {
                  refresh: 2,
              }
            : {},
    });

    return res.data ? res.data : [];
};

// ---------------------------------------------------------------------------------
// The search boxes call this, not `findInstruments`.
//
// With the switch off (the default - see lib/omnisearch/flag.ts) it *is*
// `findInstruments`: same request, same response. With it on, it asks the omnisearch
// endpoint and hands back the same `FindInstruments[]` shape, so the dropdown renders
// exactly as it does today - only the answers change: spaced tickers (`volv b`),
// dotted ones (`brk.b`), plurals (`volvo cars`), and one row per company instead of
// one per listing.
//
// It never makes search worse than today. It falls back to the legacy endpoint when:
//  - the request fails or is slow (2.5 s);
//  - the worker that answered has not built its index yet (`index.built === false`).
//    Measured on production: the index is per gunicorn worker, and a worker that has
//    not yet served a search answers empty for the ~40 s its first build takes;
//  - nothing instrument-shaped came back.
//
// Measures, datasets, people and deep links (`tsla short interest`) are dropped here,
// because this dropdown can only render an instrument row. The header box shows them
// through `searchOmni` below and `components/omni-autocomplete`.

const OMNISEARCH_PATH = "/interface/api/search/";
const OMNISEARCH_TIMEOUT_MS = 2500;

const SECURITY_TYPE: Partial<Record<OmniKind, string>> = {
    company: "Equity",
    etf: "ETF",
    fund: "Fund",
    index: "Index",
    fx: "FX",
    commodity: "Commodity",
    reit: "REIT",
};

let announced = false;

/** Map one omnisearch row onto the legacy shape, or null if this dropdown cannot show it. */
export function toFindInstrument(row: OmniResult): (FindInstruments & { isin?: string; ortex_sid?: number }) | null {
    const securityType = SECURITY_TYPE[row.kind];
    const { ticker, exchange } = row.payload;
    if (!securityType || !ticker || !exchange) return null;
    return {
        // NOT the legacy meaning: legacy `value` is the screening Stock id, and some pickers
        // send it to endpoints that expect one (portfolio add, notification subscriptions).
        // Those boxes pass `legacySearch` to SearchBar. Read `tradingitem_id` for the listing.
        value: row.payload.tradingitemId ?? row.payload.tid ?? 0,
        name: row.title,
        ticker,
        exchange,
        sort_value: row.score,
        security_type: securityType,
        // The legacy endpoint never populated this either; logos come from the ids below.
        logo: undefined,
        // CIQ-aligned, the same id space the legacy `security_id` used - so the legacy
        // logo origin and the missing-logo report keep working unchanged.
        security_id: row.payload.securityId ?? null,
        tradingitem_id: row.payload.tradingitemId ?? null,
        company_id: row.payload.companyId ?? null,
        // Not on the legacy response. lib/logos.ts prefers ISIN, then the ORTEX sid, over
        // ticker:exchange - so the edge logo service resolves more of these rows.
        isin: row.payload.isin || undefined,
        ortex_sid: row.payload.sid,
    };
}

export const searchInstruments = async (text: string): Promise<FindInstruments[]> => {
    if (!isOmnisearchEnabled()) return findInstruments(text);

    if (!announced && typeof console !== "undefined") {
        announced = true;
        console.info("[omnisearch] on for this browser - add ?omnisearch=0 to any URL to switch back");
    }

    try {
        const res = await axios.get<OmniResponse>(OMNISEARCH_PATH, {
            params: { q: text, limit: 15 },
            timeout: OMNISEARCH_TIMEOUT_MS,
        });
        const body = res.data;
        if (!body?.index?.built) return findInstruments(text);
        const rows = (body.results ?? []).map(toFindInstrument).filter((row): row is NonNullable<typeof row> => row !== null);
        return rows.length ? rows : findInstruments(text);
    } catch {
        return findInstruments(text);
    }
};

// ---------------------------------------------------------------------------------
// The rich dropdown (components/omni-autocomplete) uses the omnisearch rows directly,
// so it can show measures, datasets, people and deep links, not just instruments.

const KIND_BY_SECURITY_TYPE: Record<string, OmniKind> = {
    Equity: "company",
    ETF: "etf",
    Fund: "fund",
    Index: "index",
    FX: "fx",
    Commodity: "commodity",
    REIT: "reit",
};

/**
 * Omnisearch rows for the rich dropdown, or `null` meaning "use the legacy dropdown for
 * this query": the switch is off, the request failed, the answering worker has not built
 * its index yet, or nothing came back. The caller then asks `findInstruments` - so search
 * is never worse than the old box.
 */
export const searchOmni = async (text: string): Promise<OmniResult[] | null> => {
    if (!isOmnisearchEnabled()) return null;
    try {
        const res = await axios.get<OmniResponse>(OMNISEARCH_PATH, {
            params: { q: text, limit: 15 },
            timeout: OMNISEARCH_TIMEOUT_MS,
        });
        const body = res.data;
        if (!body?.index?.built) return null;
        return body.results && body.results.length ? body.results : null;
    } catch {
        return null;
    }
};

/**
 * A search-history entry (stored as the legacy shape in user settings) as an omnisearch
 * row, so the rich dropdown can show history the same way as results.
 */
export function fromFindInstrument(item: FindInstruments): OmniResult {
    const kind = KIND_BY_SECURITY_TYPE[item.security_type] ?? "company";
    const route = `/s/${item.exchange || " "}/${item.ticker}`;
    return {
        id: `history:${route}`,
        key: item.company_id ? `company:${item.company_id}` : "",
        kind,
        title: item.name,
        subtitle: `${item.exchange}:${item.ticker}`,
        route,
        score: 0,
        otherListings: 0,
        payload: {
            ticker: item.ticker,
            exchange: item.exchange,
            securityId: item.security_id ?? undefined,
            tradingitemId: item.tradingitem_id ?? undefined,
            companyId: item.company_id ?? undefined,
            isin: (item as FindInstruments & { isin?: string }).isin,
        },
    };
}
